import { logout } from "@/api/login/login-services";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { resolveDefaultLanguage } from "@/lib/language-utils";
import { useStore } from "@/store";
import {
  CircleUserRound,
  EllipsisVertical,
  Globe,
  LogOut,
  User,
} from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const UserNav = () => {
    const loggedInUser = useStore((state) => state.loggedInUser);
  const languages = useStore((state) => state.languages);
  const languageList = useStore((state) => state.languageList);
  const selectedLanguage = useStore((state) => state.selectedLanguage);
  const setSelectedLanguage = useStore((state) => state.setSelectedLanguage);
  const { i18n, t } = useTranslation();

  const changeLanguage = (lng) => {
    const normalizedLanguage = lng.toLowerCase(); // Convertir a minúsculas
    i18n.changeLanguage(normalizedLanguage);
    setSelectedLanguage(normalizedLanguage);
  };

  // No necesitamos llamar a getLanguages aquí ya que se llama desde MainLayout

  // Resuelve y sincroniza el idioma de la interfaz con la configuración del tenant:
  // - si el idioma seleccionado (navegador o persistido) está entre los activos, se usa ese;
  // - si no está activo, se resuelve al primer idioma activo (p. ej. un tenant con solo
  //   inglés activo debe mostrarse en inglés aunque el navegador sea de otro idioma).
  useEffect(() => {
    const activeLanguages = Array.isArray(languageList)
      ? languageList
      : Array.isArray(languageList?.languages)
        ? languageList.languages
        : [];

    if (activeLanguages.length === 0) {
      return;
    }

    const currentLanguage = selectedLanguage || i18n.language;
    const resolvedLanguage =
      resolveDefaultLanguage(activeLanguages, currentLanguage) ||
      currentLanguage;

    if (resolvedLanguage && i18n.language !== resolvedLanguage) {
      i18n.changeLanguage(resolvedLanguage);
      setSelectedLanguage(resolvedLanguage);
    }
  }, [languageList, selectedLanguage, i18n, setSelectedLanguage]);


  const handleLogout = async () => {
    await logout();
    // Add navigation logic here if needed
  };

  return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild={true}>
          <button className="relative whitespace-nowrap focus:outline-none transition-all text-center group flex w-full items-center justify-between rounded-lg p-2 text-sm font-medium text-slate-500 hover:bg-slate-100/50">
          <span className="flex items-center gap-3">
            <Avatar>
              <AvatarImage />
              <AvatarFallback className="rounded-full group-hover:border-primary transition-all bg-white border">
                <CircleUserRound />
              </AvatarFallback>
            </Avatar>
            <span>{loggedInUser.name}</span>
          </span>
            <EllipsisVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuLabel>{loggedInUser.email}</DropdownMenuLabel>
          <DropdownMenuGroup>
            <Link to={"/admin/profile"} className="flex">
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                <span>{t("MainLayout.userNav.profile")}</span>
              </DropdownMenuItem>
            </Link>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Globe className="mr-2 h-4 w-4" />
                <span>{t("MainLayout.userNav.language")}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {languages && Array.isArray(languages) && languages.length > 0 &&
                      languages.map((language) => {
                        const active = i18n.language === language.code;

                        return (
                            <DropdownMenuItem
                                key={language.id}
                                className="cursor-pointer"
                                onClick={() => changeLanguage(language.code)}
                                disabled={active}
                            >
                              <span className="w-full">{language.name}</span>
                            </DropdownMenuItem>
                        );
                      })}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </DropdownMenuGroup>
          <DropdownMenuGroup />
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t("MainLayout.userNav.logout")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
  );
};

export { UserNav };
